import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Save, Upload, Image as ImageIcon } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, apiUrl } from "@/lib/api";
import { COUNTRIES, INDUSTRIES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function CreatorProfile() {
  const { t } = useTranslation();
  const [p, setP] = useState({});
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/api/creator/profile");
      setP(data);
    })();
  }, []);

  const set = (patch) => setP((v) => ({ ...v, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/api/creator/profile", p);
      setP(data);
      toast.success(t("creator_profile.toast_saved"));
    } catch (e) {
      toast.error(t("creator_profile.toast_save_error"));
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ photo_url: apiUrl(data.url) });
    } catch (e) {
      toast.error(t("creator_profile.toast_upload_error"));
    }
  };

  return (
    <DashboardLayout
      title={t("creator_profile.page_title")}
      subtitle={t("creator_profile.page_subtitle")}
      actions={
        <Button onClick={save} disabled={saving} data-testid="button-save-creator-profile" className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
          <Save size={16} className="mr-2" /> {saving ? t("creator_profile.saving") : t("creator_profile.save")}
        </Button>
      }
    >
      <Tabs defaultValue="identity">
        <TabsList className="mb-6 bg-slate-100 rounded-full">
          <TabsTrigger value="identity">{t("creator_profile.tab_identity")}</TabsTrigger>
          <TabsTrigger value="professional">{t("creator_profile.tab_professional")}</TabsTrigger>
          <TabsTrigger value="company">{t("creator_profile.tab_company")}</TabsTrigger>
          <TabsTrigger value="preferences">{t("creator_profile.tab_preferences")}</TabsTrigger>
        </TabsList>

        <TabsContent value="identity" className="space-y-6">
          <div className="kpi-card p-6">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
                {p.photo_url ? <img src={p.photo_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="text-slate-400" />}
              </div>
              <div>
                <input ref={photoRef} type="file" accept="image/*" hidden onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                <Button variant="outline" onClick={() => photoRef.current?.click()}><Upload size={14} className="mr-2" /> {t("creator_profile.upload_photo")}</Button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5 mt-6">
              <Field label={t("creator_profile.field_name")}><Input value={p.display_name || ""} onChange={(e) => set({ display_name: e.target.value })} data-testid="input-creator-name" /></Field>
              <Field label={t("creator_profile.field_country")}>
                <Select value={p.country || ""} onValueChange={(v) => set({ country: v })}>
                  <SelectTrigger><SelectValue placeholder={t("creator_profile.field_country_placeholder")} /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={t("creator_profile.field_city")}><Input value={p.city || ""} onChange={(e) => set({ city: e.target.value })} /></Field>
              <Field label={t("creator_profile.field_phone")}><Input value={p.phone || ""} onChange={(e) => set({ phone: e.target.value })} /></Field>
              <Field label={t("creator_profile.field_website")}><Input value={p.website || ""} onChange={(e) => set({ website: e.target.value })} /></Field>
              <Field label={t("creator_profile.field_imdb")}><Input value={p.imdb || ""} onChange={(e) => set({ imdb: e.target.value })} /></Field>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="professional" className="space-y-6">
          <div className="kpi-card p-6 space-y-5">
            <Field label={t("creator_profile.field_primary_role")}><Input value={p.primary_role || ""} onChange={(e) => set({ primary_role: e.target.value })} placeholder={t("creator_profile.field_primary_role_placeholder")} /></Field>
            <Field label={t("creator_profile.field_bio_short")}><Textarea rows={2} value={p.bio_short || ""} onChange={(e) => set({ bio_short: e.target.value })} /></Field>
            <Field label={t("creator_profile.field_bio_long")}><Textarea rows={6} value={p.bio_long || ""} onChange={(e) => set({ bio_long: e.target.value })} /></Field>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={t("creator_profile.field_years_experience")}><Input type="number" value={p.years_experience ?? ""} onChange={(e) => set({ years_experience: e.target.value ? parseInt(e.target.value, 10) : null })} /></Field>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="company" className="space-y-6">
          <div className="kpi-card p-6 grid md:grid-cols-2 gap-5">
            <Field label={t("creator_profile.field_account_type")}>
              <Select value={p.account_type || "person"} onValueChange={(v) => set({ account_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">{t("creator_profile.account_type_person")}</SelectItem>
                  <SelectItem value="company">{t("creator_profile.account_type_company")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("creator_profile.field_company_commercial")}><Input value={p.company_commercial_name || ""} onChange={(e) => set({ company_commercial_name: e.target.value })} /></Field>
            <Field label={t("creator_profile.field_company_legal")}><Input value={p.company_name || ""} onChange={(e) => set({ company_name: e.target.value })} /></Field>
            <Field label={t("creator_profile.field_company_tax_id")}><Input value={p.company_tax_id || ""} onChange={(e) => set({ company_tax_id: e.target.value })} /></Field>
            <Field label={t("creator_profile.field_company_founded")}><Input type="number" value={p.company_founded_year ?? ""} onChange={(e) => set({ company_founded_year: e.target.value ? parseInt(e.target.value, 10) : null })} /></Field>
            <Field label={t("creator_profile.field_company_team_size")}><Input value={p.company_team_size || ""} onChange={(e) => set({ company_team_size: e.target.value })} /></Field>
            <Field label={t("creator_profile.field_company_website")}><Input value={p.company_website || ""} onChange={(e) => set({ company_website: e.target.value })} /></Field>
          </div>
        </TabsContent>

        <TabsContent value="preferences" className="space-y-6">
          <div className="kpi-card p-6 space-y-5">
            <Field label={t("creator_profile.field_visibility")}>
              <Select value={p.visibility || "public"} onValueChange={(v) => set({ visibility: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">{t("creator_profile.visibility_public")}</SelectItem>
                  <SelectItem value="private">{t("creator_profile.visibility_private")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
